import { View, type ViewStyle } from 'react-native'
import { Pressable } from './Pressable'
import { useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import type { Session } from '@supabase/supabase-js'
import { ChevronLeft } from '@/components/ui/icons'
import { GlassSurface } from '@/components/ui/GlassSurface'
import { impact, ImpactStyle } from '@/lib/utils/haptics'
import { useTheme } from '../../lib/hooks/use-theme'
import { INTERACTION, useSemanticColors } from '../../lib/theme/tokens'
import { authKeys } from '../../lib/hooks/use-auth'

const BACK_BUTTON_SIZE = INTERACTION.minTouchTarget
const BACK_BUTTON_ICON_SIZE = 24

interface BackButtonProps {
  /** Custom press handler. Defaults to `router.back()`. */
  onPress?: () => void
  /**
   * Explicit destination if there is no history to go back to. When
   * `preferFallback` is true, this becomes the canonical destination.
   */
  fallbackHref?: string
  preferFallback?: boolean
  testID?: string
  style?: ViewStyle
  size?: number
  /**
   * Pressable area, when it must be bigger than the disc.
   *
   * `hitSlop` already does this on native and react-native-web drops it, so on
   * web the tap area is exactly the disc. That is fine in a header, where the
   * surrounding pixels do nothing. It is not fine floating over the full map,
   * where every pixel around the control pans the map: a near-miss does not
   * do nothing, it moves the thing behind it.
   *
   * The disc stays `size` so the control still looks like every other back
   * button in the app — only the target grows.
   */
  touchSize?: number
}

/** Canonical iOS-style circular back control for screen headers and floating chrome. */
export function BackButton({
  onPress,
  fallbackHref,
  preferFallback = false,
  testID,
  style,
  size,
  touchSize,
}: BackButtonProps) {
  const router = useRouter()
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const queryClient = useQueryClient()

  const handlePress = () => {
    impact(ImpactStyle.Light)
    if (onPress) {
      onPress()
      return
    }

    if (preferFallback && fallbackHref) {
      router.replace(fallbackHref as never)
      return
    }

    // Deep-link safe: when there is no history, `router.back()` is a silent
    // no-op. Keep the default auth-aware so public/legal/auth paths do not
    // bounce signed-in users to the welcome surface.
    if (router.canGoBack()) {
      router.back()
      return
    }
    const session = queryClient.getQueryData<Session | null>(authKeys.session())
    const isAuthenticated = !!session?.user
    const destination = fallbackHref ?? (isAuthenticated ? '/(tabs)/profile' : '/(auth)/welcome')
    router.replace(destination as never)
  }

  const buttonSize = size ?? BACK_BUTTON_SIZE
  const buttonRadius = buttonSize / 2

  // Only a caller that asks for a bigger target changes shape. Every other
  // screen renders the exact tree it rendered before — the disc styles stay on
  // the outer View, the chevron stays a direct child of the Pressable — so
  // this cannot move a pixel on the nineteen call sites that were already
  // right.
  const target = Math.max(buttonSize, touchSize ?? buttonSize)
  const expanded = target > buttonSize
  const disc = (
    <GlassSurface
      tone="regular"
      visualRole="chrome"
      interactive
      radius={buttonRadius}
      style={{
        width: buttonSize,
        height: buttonSize,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ChevronLeft size={BACK_BUTTON_ICON_SIZE} color={colors.textPrimary} strokeWidth={2.35} />
    </GlassSurface>
  )

  return (
    <View
      style={[
        {
          width: target,
          height: target,
          alignItems: 'center',
          justifyContent: 'center',
        },
        style,
      ]}
    >
      <Pressable
        onPress={handlePress}
        style={({ pressed }) => ({
          width: target,
          height: target,
          borderRadius: expanded ? target / 2 : buttonRadius,
          alignItems: 'center',
          justifyContent: 'center',
          opacity: pressed ? 0.82 : 1,
          transform: [{ scale: pressed ? 0.96 : 1 }],
        })}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={t('common.back')}
        hitSlop={INTERACTION.iconHitSlop}
      >
        {disc}
      </Pressable>
    </View>
  )
}
